/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useLocale } from "@i18n/react";
import { useEffect, useRef } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { radius, spacing } from "../../../../../global/emotion";
import { Button } from "../Button/Button.emotion";
import { Mask } from "../Mask/Mask.emotion";
import { Text } from "../Text/Text.emotion";

const cssButton: Interpolation<ThemeType> = (theme) => ({
  borderRadius: radius.s,
  padding: `${spacing.none} ${spacing.ms}`,
});

const cssButtonInactive: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});

const cssText: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  padding: `${spacing.none} ${spacing.s}`,
});

const cssMonthSelectorRoot: Interpolation = {
  position: "relative",
  width: "100%",
};

const cssMonthSelectorScroll: Interpolation = {
  display: "flex",
  overflowX: "auto",
  gap: spacing.s,
  padding: `${spacing.xxs} ${spacing.ms}`,
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  alignItems: "center",
};

const cssMaskLeft: Interpolation = {
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
};

const cssMaskRight: Interpolation = {
  position: "absolute",
  top: 0,
  bottom: 0,
  right: 0,
};

type MonthItem =
  | { type: "month"; date: Date; key: string }
  | { type: "year"; year: number; key: string };

const RANGE = 5;

function buildMonthsAroundYear(centerYear: number): MonthItem[] {
  const items: MonthItem[] = [];
  for (let year = centerYear - RANGE; year <= centerYear + RANGE; year++) {
    items.push({ type: "year", year, key: `year-${year}` });
    for (let month = 0; month < 12; month++) {
      items.push({
        type: "month",
        date: new Date(year, month, 1),
        key: `${year}-${month}`,
      });
    }
  }
  return items;
}

/** Fixed range around current year so scroll position/layout stays stable. */
const MONTH_ITEMS: MonthItem[] = (() => {
  const centerYear = new Date().getFullYear();
  return buildMonthsAroundYear(centerYear);
})();

type Props = {
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
};

export function MonthSelector({ currentMonth, onMonthChange }: Props) {
  const activeRef = useRef<HTMLButtonElement>(null);
  const isMounted = useRef(false);
  const locale = useLocale();

  useEffect(() => {
    const behavior: ScrollBehavior = isMounted.current ? "smooth" : "auto";
    isMounted.current = true;
    activeRef.current?.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior,
    });
  }, [currentMonth]);

  return (
    <div css={cssMonthSelectorRoot}>
      <Mask
        size="xs"
        color="background"
        direction="to right"
        orientation="vertical"
        css={cssMaskLeft}
      />
      <div css={cssMonthSelectorScroll}>
        {MONTH_ITEMS.map((item) => {
          if (item.type === "year") {
            return (
              <Text key={item.key} size="s" type="button" css={cssText}>
                {item.year}
              </Text>
            );
          }

          const isActive =
            item.date.getFullYear() === currentMonth.getFullYear() &&
            item.date.getMonth() === currentMonth.getMonth();

          return (
            <Button
              key={item.key}
              ref={isActive ? activeRef : undefined}
              onClick={() => onMonthChange(item.date)}
              size="s"
              variant={isActive ? "primary" : "outline"}
              css={[cssButton, !isActive && cssButtonInactive]}
            >
              {item.date.toLocaleString(locale, { month: "short" })}
            </Button>
          );
        })}
      </div>
      <Mask
        size="xs"
        color="background"
        direction="to left"
        orientation="vertical"
        css={cssMaskRight}
      />
    </div>
  );
}
